import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import type { Content, TableCell, TDocumentDefinitions } from 'pdfmake/interfaces'
import { DateTime } from 'luxon'
import { moneyFormat } from '@/components/helpers/formats'
import { isDonorAddedDonation } from '@/helpers/taxPackDonations'
import { TAX_PACK_PDF_COPY as copy } from '@/services/taxPackPdf/taxPackPdfCopy'
import { SWIFTAID_LOGO_SVG } from '@/services/taxPackPdf/taxPackPdfLogo'
import type { TaxPackData, TaxPackExportOptions } from '@/types/taxPack'
import type { DonorDonation } from '@/types/donorFinancialYear'

type PdfMakeOutput = {
  getBlob: () => Promise<Blob>
  download: (filename?: string) => Promise<void>
}

type PdfMakeClient = {
  addVirtualFileSystem?: (vfs: Record<string, string>) => void
  addFonts?: (fonts: Record<string, Record<string, string>>) => void
  vfs?: Record<string, string>
  createPdf: (definition: TDocumentDefinitions) => PdfMakeOutput
}

const pdfMakeClient = pdfMake as PdfMakeClient

const ROBOTO_FONT_DEFINITION = {
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Medium.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-MediumItalic.ttf',
  },
}

let pdfMakeFontsReady = false

/** pdfmake 0.3 uses addVirtualFileSystem/addFonts; 0.2 used pdfMake.vfs. */
function ensurePdfMakeFonts(): void {
  if (pdfMakeFontsReady) return

  const vfs = pdfFonts as Record<string, string>

  if (typeof pdfMakeClient.addVirtualFileSystem === 'function') {
    pdfMakeClient.addVirtualFileSystem(vfs)
    pdfMakeClient.addFonts?.(ROBOTO_FONT_DEFINITION)
  } else {
    pdfMakeClient.vfs = vfs
  }

  pdfMakeFontsReady = true
}

const INK = '#002d43'
const MUTED = '#5a7280'
const BORDER = '#dce6ea'
const SURFACE = '#f4f8fa'
const BLUE_PALE = '#ecf7fe'
const PANEL_PAD_X = 10
const PANEL_PAD_Y = 8
const PANEL_GAP = 12
const DONOR_SECTION_GAP = 22
const NESTED_PAD_X = 8
const NESTED_PAD_Y = 8

function formatMoneyPence(pence: number): string {
  const formatted = moneyFormat(pence)
  return formatted ? `£${formatted}` : '£0.00'
}

function formatDonationDate(iso: string): string {
  try {
    return DateTime.fromISO(iso).toFormat('d MMM yyyy')
  } catch {
    return iso
  }
}

function formatIssuedDate(iso: string): string {
  try {
    return DateTime.fromISO(iso).toFormat('d MMM yyyy')
  } catch {
    return iso
  }
}

function borderedPanel(
  inner: Content,
  options: {
    fill?: string
    borderColor?: string
    marginBottom?: number
    paddingX?: number
    paddingY?: number
  } = {}
): Content {
  const {
    fill = '#ffffff',
    borderColor = BORDER,
    marginBottom = PANEL_GAP,
    paddingX = PANEL_PAD_X,
    paddingY = PANEL_PAD_Y,
  } = options

  return {
    table: {
      widths: ['*'],
      body: [[inner]],
    },
    layout: {
      fillColor: () => fill,
      hLineWidth: () => 1,
      vLineWidth: () => 1,
      hLineColor: () => borderColor,
      vLineColor: () => borderColor,
      paddingLeft: () => paddingX,
      paddingRight: () => paddingX,
      paddingTop: () => paddingY,
      paddingBottom: () => paddingY,
    },
    margin: [0, 0, 0, marginBottom] as [number, number, number, number],
  }
}

function brandHeader(pack: TaxPackData): Content {
  return {
    columns: [
      {
        svg: SWIFTAID_LOGO_SVG,
        width: 112,
        margin: [0, 0, 0, 0] as [number, number, number, number],
      },
      {
        width: '*',
        stack: [
          { text: copy.documentTitle, style: 'documentMainTitle', alignment: 'right' },
          {
            text: pack.taxYearTitle,
            style: 'headerTaxYear',
            alignment: 'right',
            margin: [0, 6, 0, 0] as [number, number, number, number],
          },
          { text: pack.dateRange, style: 'headerDateRange', alignment: 'right' },
        ],
      },
    ],
    margin: [0, 0, 0, PANEL_GAP] as [number, number, number, number],
  }
}

function metaRow(label: string, value: string) {
  return {
    columns: [
      { width: 96, text: label, style: 'metaRowLabel' },
      { width: '*', text: value, style: 'metaRowValue' },
    ],
    margin: [0, 0, 0, 5] as [number, number, number, number],
  }
}

function donorAndStatementCard(pack: TaxPackData): Content {
  const donor = pack.donor
  const addressParts = [
    donor.addressLine1,
    donor.addressLine2,
    [donor.city, donor.postcode].filter(Boolean).join(' '),
  ].filter((line): line is string => Boolean(line && line.trim()))

  return borderedPanel(
    {
      columns: [
        {
          width: '48%',
          stack: [
            { text: copy.donorSection, style: 'metaSectionTitle' },
            { text: `${donor.firstname} ${donor.lastname}`, style: 'metaValueStrong' },
            { text: addressParts.join(', '), style: 'metaValue' },
          ],
        },
        {
          width: '52%',
          stack: [
            { text: copy.statementSection, style: 'metaSectionTitle' },
            metaRow(copy.issued, formatIssuedDate(pack.issuedStatement.issued)),
            metaRow(copy.refNo, pack.issuedStatement.id),
            metaRow(copy.donorIntermediary, copy.intermediaryName),
          ],
        },
      ],
      columnGap: 16,
    },
    { marginBottom: DONOR_SECTION_GAP }
  )
}

function statColumn(label: string, value: string) {
  return {
    width: '*' as const,
    stack: [
      { text: label, style: 'statLabel' },
      { text: value, style: 'statValue' },
    ],
  }
}

function swiftaidExclusionRich(totalDonated: string, totalGiftAid: string) {
  return {
    text: [
      { text: 'This total of ', style: 'infoText' },
      { text: `£${totalDonated}`, style: 'infoEmphasis' },
      {
        text: ' only includes donations processed through Swiftaid. It does not include gifts made through other donor intermediaries on your behalf, or donations given directly to charities. ',
        style: 'infoText',
      },
      { text: `£${totalGiftAid}`, style: 'infoEmphasis' },
      {
        text: ' is the maximum amount of Gift Aid that charities can claim on these donations.',
        style: 'infoText',
      },
    ],
    lineHeight: 1.4,
  }
}

type FootnoteItem =
  | { text: string; style?: string; lineHeight?: number }
  | ReturnType<typeof swiftaidExclusionRich>

function cardFootnotes(notes: FootnoteItem[]): Content {
  const items = notes.map((note, index) => ({
    ...note,
    margin: [0, index === 0 ? 0 : 6, 0, 0] as [number, number, number, number],
  }))

  return {
    stack: items as Content[],
    margin: [0, 4, 0, 0] as [number, number, number, number],
  }
}

function cardDivider(): Content {
  return {
    table: {
      widths: ['*'],
      body: [
        [
          {
            text: '',
            border: [false, true, false, false],
            borderColor: [BORDER, BORDER, BORDER, BORDER],
          },
        ],
      ],
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 4,
      paddingBottom: () => 0,
    },
  }
}

/** Wraps nested panels so pdfmake keeps them within the parent width (avoids right-margin bleed). */
function constrainedWidth(content: Content): Content {
  return {
    columns: [{ width: '*', stack: [content] }],
  }
}

function nestedSummaryCard(inner: Content, isLast = false): Content {
  return {
    stack: [
      constrainedWidth(
        borderedPanel(inner, {
          fill: '#ffffff',
          marginBottom: 0,
          paddingX: NESTED_PAD_X,
          paddingY: NESTED_PAD_Y,
        })
      ),
    ],
    margin: [0, 0, 0, isLast ? 0 : 8] as [number, number, number, number],
  }
}

function swiftaidStatementInner(pack: TaxPackData): Content {
  return {
    stack: [
      { text: copy.swiftaidStatementTitle, style: 'cardTitle' },
      {
        columns: [
          statColumn(
            copy.swiftaidGiftAidedDonations,
            formatMoneyPence(pack.swiftaidTotalDonated)
          ),
          statColumn(
            copy.swiftaidMaximumGiftAid,
            formatMoneyPence(pack.swiftaidTotalGiftAid)
          ),
        ],
        columnGap: 12,
        margin: [0, 6, 0, 0] as [number, number, number, number],
      },
      cardDivider(),
      cardFootnotes([
        swiftaidExclusionRich(
          moneyFormat(pack.swiftaidTotalDonated) ?? '0.00',
          moneyFormat(pack.swiftaidTotalGiftAid) ?? '0.00'
        ),
        { text: copy.taxResponsibility, style: 'infoText' },
      ]),
    ],
  }
}

function addedByYouInner(pack: TaxPackData): Content {
  return {
    stack: [
      { text: copy.addedByYouTitle, style: 'cardTitle' },
      {
        columns: [
          statColumn(copy.totalDonated, formatMoneyPence(pack.manualTotalDonated)),
          statColumn(copy.giftAid, formatMoneyPence(pack.manualTotalGiftAid)),
        ],
        columnGap: 12,
        margin: [0, 6, 0, 0] as [number, number, number, number],
      },
      cardDivider(),
      cardFootnotes([{ text: copy.addedByYouIntro, style: 'infoText' }]),
    ],
  }
}

function swiftaidStatementCard(pack: TaxPackData): Content {
  return borderedPanel(swiftaidStatementInner(pack))
}

function combinedGiftAidGroup(pack: TaxPackData): Content {
  return borderedPanel(
    {
      stack: [
        constrainedWidth({
          columns: [
            {
              width: '*',
              stack: [
                { text: copy.totalDonated, style: 'statLabel' },
                {
                  text: formatMoneyPence(pack.totalGivingRecorded),
                  style: 'combinedGiftAidValue',
                  margin: [0, 2, 0, 0] as [number, number, number, number],
                },
              ],
            },
            {
              width: '*',
              stack: [
                { text: copy.combinedTotalGiftAid, style: 'statLabel' },
                {
                  text: formatMoneyPence(pack.combinedTotalGiftAid),
                  style: 'combinedGiftAidValue',
                  margin: [0, 2, 0, 0] as [number, number, number, number],
                },
              ],
            },
          ],
          columnGap: 16,
          margin: [0, 0, 0, 10] as [number, number, number, number],
        }),
        {
          stack: [
            nestedSummaryCard(swiftaidStatementInner(pack)),
            nestedSummaryCard(addedByYouInner(pack), true),
          ],
          margin: [0, 0, 0, 0] as [number, number, number, number],
        },
      ],
    },
    { borderColor: INK, fill: BLUE_PALE, marginBottom: 0, paddingX: 12, paddingY: 10 }
  )
}

function buildSummaryPanels(
  pack: TaxPackData,
  includeManual: boolean,
  isDetailed: boolean
): Content {
  const panel =
    includeManual && pack.hasManualSection
      ? combinedGiftAidGroup(pack)
      : swiftaidStatementCard(pack)

  return {
    stack: [panel],
    ...(isDetailed ? { pageBreak: 'after' as const } : {}),
  }
}

function sectionBlock(title: string): Content {
  return {
    text: title,
    style: 'sectionTitle',
    margin: [0, 0, 0, 8] as [number, number, number, number],
  }
}

function tableLayout() {
  return {
    fillColor: (rowIndex: number) => {
      if (rowIndex === 0) return BLUE_PALE
      return rowIndex % 2 === 0 ? '#ffffff' : SURFACE
    },
    hLineWidth: (rowIndex: number, node: { table: { body: unknown[] } }) =>
      rowIndex === 0 || rowIndex === node.table.body.length ? 1 : 0.5,
    vLineWidth: () => 0,
    hLineColor: (rowIndex: number) => (rowIndex <= 1 ? BORDER : '#e7eff2'),
    paddingLeft: () => 6,
    paddingRight: () => 6,
    paddingTop: () => 5,
    paddingBottom: () => 5,
  }
}

function donationTable(options: {
  donations: DonorDonation[]
  showAddedByYouColumn: boolean
}): Content {
  const { donations, showAddedByYouColumn } = options

  if (!donations.length) {
    return { text: copy.noDonationsInYear, style: 'bodyMuted', margin: [0, 0, 0, 14] }
  }

  const header: TableCell[] = showAddedByYouColumn
    ? [
        { text: copy.tableCharity, style: 'tableHeader' },
        { text: copy.tableDate, style: 'tableHeader' },
        { text: '', style: 'tableHeader' },
        { text: copy.tableAmount, style: 'tableHeader', alignment: 'right' },
        { text: copy.tableGiftAid, style: 'tableHeader', alignment: 'right' },
      ]
    : [
        { text: copy.tableCharity, style: 'tableHeader' },
        { text: copy.tableDate, style: 'tableHeader' },
        { text: copy.tableAmount, style: 'tableHeader', alignment: 'right' },
        { text: copy.tableGiftAid, style: 'tableHeader', alignment: 'right' },
      ]

  const body: TableCell[][] = donations.map((donation) => {
    const isAddedByDonor = isDonorAddedDonation(donation)
    const row: TableCell[] = [
      { text: donation.charity, style: 'tableCell' },
      { text: formatDonationDate(donation.time), style: 'tableCellMuted', noWrap: true },
    ]

    if (showAddedByYouColumn) {
      row.push({
        text: isAddedByDonor ? copy.sourceAddedByYou : '',
        style: isAddedByDonor ? 'tableCellAddedByYou' : 'tableCellMuted',
        noWrap: true,
      })
    }

    row.push(
      { text: formatMoneyPence(donation.amount), style: 'tableCellAmount' },
      {
        text: donation.giftaid ? `+${formatMoneyPence(donation.giftaid)}` : copy.giftAidNa,
        style: donation.giftaid ? 'tableCellGiftAid' : 'tableCellGiftAidNa',
      }
    )

    return row
  })

  return {
    table: {
      headerRows: 1,
      widths: showAddedByYouColumn
        ? ['*', 54, 58, 52, 52]
        : ['*', 54, 52, 52],
      body: [header, ...body],
    },
    layout: tableLayout(),
    margin: [0, 0, 0, 16] as [number, number, number, number],
  }
}

function buildDonationsSection(
  pack: TaxPackData,
  isDetailed: boolean,
  includeManual: boolean
): Content[] {
  if (!isDetailed) return []

  const donations = includeManual ? pack.combinedDonations : pack.swiftaidDonations

  return [
    sectionBlock(copy.itemisedHeading),
    donationTable({
      donations,
      showAddedByYouColumn: includeManual && pack.hasManualSection,
    }),
  ]
}

export function buildTaxPackDocumentDefinition(
  pack: TaxPackData,
  options: TaxPackExportOptions
): TDocumentDefinitions {
  const isDetailed = options.detailLevel === 'detailed'
  const includeManual = options.includeDonorAdded && pack.hasManualSection

  const content: Content[] = [
    brandHeader(pack),
    donorAndStatementCard(pack),
    buildSummaryPanels(pack, includeManual, isDetailed),
    ...buildDonationsSection(pack, isDetailed, includeManual),
  ]

  return {
    pageSize: 'A4',
    pageMargins: [44, 40, 44, 48],
    defaultStyle: {
      font: 'Roboto',
      fontSize: 9.5,
      color: INK,
      lineHeight: 1.35,
    },
    footer: (currentPage, pageCount) => ({
      columns: [
        {
          stack: [
            { text: copy.brandFooter, style: 'footer' },
            {
              text: `${copy.refNo} ${pack.issuedStatement.id}`,
              style: 'footerRef',
              margin: [0, 2, 0, 0] as [number, number, number, number],
            },
          ],
        },
        {
          text: `Page ${currentPage} of ${pageCount}`,
          style: 'footer',
          alignment: 'right',
        },
      ],
      margin: [44, 6, 44, 20] as [number, number, number, number],
    }),
    styles: {
      documentMainTitle: { fontSize: 16, bold: true, color: INK },
      headerTaxYear: { fontSize: 12, bold: true, color: INK },
      headerDateRange: { fontSize: 9, color: MUTED, margin: [0, 2, 0, 0] as [number, number, number, number] },
      groupTitle: { fontSize: 12, bold: true, color: INK },
      cardTitle: { fontSize: 10.5, bold: true, color: INK },
      combinedGiftAidValue: {
        fontSize: 20,
        bold: true,
        color: INK,
      },
      statLabel: { fontSize: 8, color: MUTED, margin: [0, 0, 0, 2] as [number, number, number, number] },
      statValue: { fontSize: 14, bold: true, color: INK },
      metaSectionTitle: {
        fontSize: 9,
        bold: true,
        color: INK,
        characterSpacing: 0.4,
        margin: [0, 0, 0, 6] as [number, number, number, number],
      },
      metaRowLabel: { fontSize: 8.5, color: MUTED },
      metaRowValue: { fontSize: 9.5, color: INK },
      metaValueStrong: { fontSize: 10.5, bold: true, margin: [0, 0, 0, 3] as [number, number, number, number] },
      metaValue: { fontSize: 9.5, color: INK, lineHeight: 1.4 },
      sectionTitle: { fontSize: 12, bold: true, color: INK },
      tableHeader: { fontSize: 7.5, bold: true, color: INK },
      tableCell: { fontSize: 9.5, color: INK },
      tableCellMuted: { fontSize: 8.5, color: MUTED },
      tableCellAddedByYou: { fontSize: 8, bold: true, color: INK },
      tableCellAmount: { fontSize: 9.5, alignment: 'right' as const, color: INK },
      tableCellGiftAid: { fontSize: 9, bold: true, alignment: 'right' as const, color: INK },
      tableCellGiftAidNa: { fontSize: 9, alignment: 'right' as const, color: MUTED },
      bodyMuted: { fontSize: 9.5, color: MUTED, italics: true },
      infoText: { fontSize: 8.5, color: MUTED, lineHeight: 1.4 },
      infoEmphasis: { fontSize: 8.5, bold: true, color: INK, lineHeight: 1.4 },
      footer: { fontSize: 7.5, color: MUTED },
      footerRef: { fontSize: 7, color: MUTED },
    },
    content,
  }
}

/** Generates a PDF Blob for download (client or Mirage mock server). */
export async function generateTaxPackPdfBlob(
  pack: TaxPackData,
  options: TaxPackExportOptions
): Promise<Blob> {
  ensurePdfMakeFonts()

  const doc = buildTaxPackDocumentDefinition(pack, options)
  const pdf = pdfMakeClient.createPdf(doc)
  const blob = await pdf.getBlob()

  if (!blob || blob.size === 0) {
    throw new Error('Generated PDF is empty')
  }

  return blob
}

/** Triggers a browser download via pdfmake (uses file-saver under the hood). */
export async function downloadTaxPackPdfFile(
  pack: TaxPackData,
  options: TaxPackExportOptions,
  filename: string
): Promise<void> {
  ensurePdfMakeFonts()

  const doc = buildTaxPackDocumentDefinition(pack, options)
  const pdf = pdfMakeClient.createPdf(doc)
  await pdf.download(filename)
}
